import { useState } from 'react';
import axios from 'axios';
import Hero from '../components/Hero';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    subject: 'General Enquiry',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const subjects = [
    'General Enquiry',
    'Individual Accreditation',
    'Institutional Accreditation',
    'Certification Programs',
    'Membership',
    'Partnership',
    'Scholarship',
    'Standards & Ethics'
  ];

  const contacts = [
    { label: 'General Enquiries', email: 'info@iccacouncil.com' },
    { label: 'Accreditation', email: 'accreditation@iccacouncil.com' },
    { label: 'Institutional Accreditation', email: 'institutions@iccacouncil.com' },
    { label: 'Certifications', email: 'certify@iccacouncil.com' },
    { label: 'Partnerships', email: 'partnerships@iccacouncil.com' },
    { label: 'Scholarships', email: 'scholarships@iccacouncil.com' },
    { label: 'Standards & Ethics', email: 'standards@iccacouncil.com' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        subject: 'General Enquiry',
        message: ''
      });
    } catch (err) {
      setError('Failed to submit form. Please try again or contact us directly via email.');
      console.error('Form submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Hero
        title="Get in Touch"
        subtitle="We welcome enquiries from coaches, training institutes, and organizations. Our team typically responds within 2 business days."
      />

      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Details */}
            <div>
              <h2 className="text-3xl font-bold text-[#1e3a5f] mb-6">Contact Details</h2>
              <div className="space-y-4">
                {contacts.map((contact, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="font-semibold text-gray-800 mb-1">{contact.label}</div>
                    <a href={`mailto:${contact.email}`} className="text-[#1e3a5f] hover:underline">
                      {contact.email}
                    </a>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-[#1e3a5f] mb-6">Send a Message</h2>
              
              {submitted ? (
                <div className="bg-green-50 border-2 border-green-500 rounded-lg p-8 text-center" data-testid="success-message">
                  <div className="text-green-500 text-5xl mb-4">✓</div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">Message Sent</h3>
                  <p className="text-gray-700 mb-4">Thank you for reaching out. We will respond within 2 business days.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-[#1e3a5f] hover:underline font-medium"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-md" data-testid="contact-form">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6">
                      {error}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent"
                        data-testid="first-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent"
                        data-testid="last-name-input"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent"
                      data-testid="email-input"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent"
                      data-testid="subject-select"
                    >
                      {subjects.map((subject) => (
                        <option key={subject} value={subject}>{subject}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent"
                      data-testid="message-input"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1e3a5f] text-white py-3 rounded-md font-semibold hover:bg-[#2d5380] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-button"
                  >
                    {loading ? 'Sending...' : 'Send Message →'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;